import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DraggableAttributes,
  type UniqueIdentifier
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode
} from 'react';
import { cn } from '@r/utils/utils.ts';

type SyntheticListenerMap = Record<string, Function> | undefined;

export type SortableDragHandleProps = {
  attributes: DraggableAttributes;
  listeners: SyntheticListenerMap;
};

export type SortableListRenderItemProps = {
  index: number;
  isDragging: boolean;
  isOverlay: boolean;
  dragHandleProps: SortableDragHandleProps;
};

export interface SortableListProps<T> {
  items: T[];
  getItemId: (item: T) => UniqueIdentifier;
  onReorder: (fromIndex: number, toIndex: number) => void;
  renderItem: (item: T, props: SortableListRenderItemProps) => ReactNode;
  /** Contenu du fantôme pendant le drag (par défaut = même rendu que l’item). */
  renderOverlay?: (item: T, props: SortableListRenderItemProps) => ReactNode;
  disabled?: boolean;
  className?: string;
  /** Distance minimale avant de démarrer un drag (évite les clics accidentels). */
  pointerActivationDistance?: number;
}

const SortableItemDisabledContext = createContext(false);

/** Poignée à placer dans `renderItem` ; à associer aux `dragHandleProps` fournis par `SortableList`. */
export const SortableDragHandle = ({
  dragHandleProps,
  className,
  children,
  ...buttonProps
}: {
  dragHandleProps: SortableDragHandleProps;
  className?: string;
  children: ReactNode;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>) => {
  const disabled = useContext(SortableItemDisabledContext);

  return (
    <button
      type="button"
      className={className}
      disabled={disabled || buttonProps.disabled}
      {...dragHandleProps.attributes}
      {...dragHandleProps.listeners}
      {...buttonProps}
    >
      {children}
    </button>
  );
};

const noopDragHandleProps: SortableDragHandleProps = {
  attributes: {} as DraggableAttributes,
  listeners: undefined
};

const SortableListItem = <T,>({
  item,
  index,
  id,
  disabled,
  renderItem
}: {
  item: T;
  index: number;
  id: UniqueIdentifier;
  disabled: boolean;
  renderItem: SortableListProps<T>['renderItem'];
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled
  });

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition
  };

  return (
    <SortableItemDisabledContext.Provider value={disabled}>
      <div ref={setNodeRef} style={style} className={cn(isDragging && 'relative z-0 opacity-40')}>
        {renderItem(item, {
          index,
          isDragging,
          isOverlay: false,
          dragHandleProps: { attributes, listeners }
        })}
      </div>
    </SortableItemDisabledContext.Provider>
  );
};

/**
 * Liste verticale réordonnable (@dnd-kit). Réutilisable dans admin, client, etc.
 *
 * @example
 * <SortableList
 *   items={rows}
 *   getItemId={(row) => row.id}
 *   onReorder={(from, to) => setRows((prev) => reorderList(prev, from, to))}
 *   renderItem={(row, { dragHandleProps }) => (
 *     <div className="flex gap-2">
 *       <SortableDragHandle dragHandleProps={dragHandleProps}>⋮⋮</SortableDragHandle>
 *       {row.label}
 *     </div>
 *   )}
 * />
 */
export const SortableList = <T,>({
  items,
  getItemId,
  onReorder,
  renderItem,
  renderOverlay,
  disabled = false,
  className,
  pointerActivationDistance = 6
}: SortableListProps<T>) => {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  const itemIds = useMemo(() => items.map((item) => getItemId(item)), [items, getItemId]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: pointerActivationDistance }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const activeItem = useMemo(() => {
    if (activeId === null) return null;
    const index = itemIds.findIndex((id) => id === activeId);
    if (index < 0) return null;
    return { item: items[index], index };
  }, [activeId, itemIds, items]);

  const handleDragStart = (event: DragStartEvent) => {
    if (disabled) return;
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (disabled || !over || active.id === over.id) return;

    const fromIndex = itemIds.indexOf(active.id);
    const toIndex = itemIds.indexOf(over.id);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

    onReorder(fromIndex, toIndex);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const overlayRenderer = renderOverlay ?? renderItem;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <div className={cn(className, activeId !== null && 'select-none')}>
          {items.map((item, index) => (
            <SortableListItem
              key={String(getItemId(item))}
              item={item}
              index={index}
              id={getItemId(item)}
              disabled={disabled}
              renderItem={renderItem}
            />
          ))}
        </div>
      </SortableContext>

      <DragOverlay dropAnimation={{ duration: 180, easing: 'ease-out' }}>
        {activeItem ? (
          <div className="rounded-xl ring-2 shadow-lg cursor-grabbing ring-primary/25">
            {overlayRenderer(activeItem.item, {
              index: activeItem.index,
              isDragging: true,
              isOverlay: true,
              dragHandleProps: noopDragHandleProps
            })}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
